import { useCallback, useState } from 'react';

import { Button } from '@hivebrite/design-system/Button';

import { displayXhrError } from 'hb-react/frontoffice/utils/globalUtils';
import { SUGGESTED_TOPICS } from 'hb-react/shared/apps/TopicsIndexV2/constants/types';
import { useTopicCardContext } from 'hb-react/shared/apps/TopicsIndexV2/contexts/TopicCardContext';
import { useTopicsIndexContext } from 'hb-react/shared/apps/TopicsIndexV2/contexts/TopicsIndexContext';
import { useTopicActionButton } from 'hb-react/shared/apps/TopicsIndexV2/hooks/useTopicActionButton';
import reloadTopics from 'hb-react/shared/apps/TopicsIndexV2/utils/reloadTopics';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';

import TopicMemberRequest from '../TopicMemberRequest';

export const JoinButtonWithoutContext = ({
  handleJoinClick,
  isConnectedUser,
  isDisabled,
  isLoading,
  labelButton,
  labelA11yButton,
  topic,
}) => {
  const { flagLogInRefactoring } = useFeatureFlag();
  const handleClick = () => {
    if (isConnectedUser) {
      handleJoinClick();
    } else if (flagLogInRefactoring) {
      const redirectTo = encodeURIComponent(Routes.topic_path(topic.id));

      window.location.href = `/login?redirect_to=${redirectTo}`;
    } else {
      // Same as app/assets/javascripts/react/frontoffice/components/LoginWrapper.jsx
      const element = document.getElementsByClassName('js-login')[0];

      element.click();
    }
  };

  return (
    <>
      {!topic.closed && (
        <Button
          isFullWidth
          isDisabled={isDisabled}
          isLoading={isLoading}
          onClick={handleClick}
          aria-label={labelA11yButton}
        >
          {!isConnectedUser ? s__('Topics|Login to join') : labelButton}
        </Button>
      )}
    </>
  );
};

const JoinButton = () => {
  const {
    currentView,
    fetchCategoryTopics,
    fetchFilteredTopics,
    fetchMyTopics,
    fetchOtherTopics,
    isConnectedUser,
    setDisplaySuccessMessageJoinPublicTopic,
  } = useTopicsIndexContext();
  const {
    displayRequestModal,
    forceDefaultLink,
    href,
    setDisplayRequestModal,
    target,
    topic,
  } = useTopicCardContext();

  const [isPendingJoinPublicTopic, setIsPendingJoinPublicTopic] =
    useState(false);

  const {
    custom_attributes_to_fill: customAttributesToFill,
    automatic_validation: automaticValidation,
    restricted_access: restrictedAccess,
    is_public_and_requires_user_to_fill_custom_attributes_to_join:
      hasCustomAttributesAndIsPublic,
  } = topic;

  const { buttonLabel, buttonA11yLabel, isMember, setIsMember } =
    useTopicActionButton(topic);

  const hasRestrictedAccessWithoutAutomaticValidation =
    restrictedAccess && !automaticValidation;

  const hasCustomAttributesToFillWithAutomaticValidation =
    automaticValidation && customAttributesToFill;

  const hasAutomaticValidationWithoutCustomAttributes =
    automaticValidation && !customAttributesToFill;

  const handleJoinClick = useCallback(() => {
    if (isConnectedUser) {
      if (
        hasRestrictedAccessWithoutAutomaticValidation ||
        hasCustomAttributesAndIsPublic ||
        hasCustomAttributesToFillWithAutomaticValidation
      ) {
        setDisplayRequestModal(true);
      } else {
        setIsPendingJoinPublicTopic(true);

        const route = hasAutomaticValidationWithoutCustomAttributes
          ? Routes.topics_topic_approvals_path(topic.id)
          : Routes.topics_topic_followings_path(topic.id);

        const approvalParams = hasAutomaticValidationWithoutCustomAttributes
          ? {
              approval: { message: __('Automatically Approved') },
            }
          : {};

        axios
          .post(route, approvalParams)
          .then(() => {
            reloadTopics({
              currentView,
              fetchCategoryTopics,
              fetchFilteredTopics,
              fetchMyTopics,
              fetchOtherTopics,
              setIsMember,
              topic,
            });

            setIsPendingJoinPublicTopic(false);
            setDisplaySuccessMessageJoinPublicTopic({
              forceDefaultLink,
              href,
              isDisplayed: true,
              name: topic.name,
              target,
            });
          })
          .catch((error) => {
            setIsPendingJoinPublicTopic(false);

            displayXhrError(error);
          });
      }
    }
  }, [topic]);

  const isJoinedSuggestedTopic =
    topic?.typeTopic === SUGGESTED_TOPICS && isMember;
  const isDisabled = topic?.joined_at !== null || isJoinedSuggestedTopic;

  return (
    <>
      <JoinButtonWithoutContext
        handleJoinClick={handleJoinClick}
        isDisabled={isDisabled}
        isLoading={isPendingJoinPublicTopic}
        isConnectedUser={isConnectedUser}
        labelButton={buttonLabel}
        labelA11yButton={buttonA11yLabel}
        topic={topic}
      />
      {displayRequestModal && (
        // @TODO: move this components: we should not use one modal by button
        <TopicMemberRequest setIsMember={setIsMember} />
      )}
    </>
  );
};

export default JoinButton;
